/* eslint-disable max-lines */

import { NgTemplateOutlet, UpperCasePipe } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import { ChangeDetectorRef, Component, inject } from '@angular/core';
import {
  FormControl,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
} from '@angular/forms';

import { Store } from '@ngrx/store';
import { deepEqual } from 'fast-equals';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { combineLatest, map, Observable, Subject, switchMap } from 'rxjs';
import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';

import { Ability } from '@core/abilities-types';
import { CurrentUserSelectors, UserSelectors } from '@core/account-data';
import { SelectAdminsAnalytics } from '@core/account-logic';
import type { CurrentUser, User } from '@core/account-types';
import { FullCandidateSelectors } from '@core/candidate-data';
import { filterBoolean, filterDefined } from '@core/common-legacy';
import { TranslatePipe } from '@core/i18n-legacy';
import { RoleSelectors } from '@core/roles-data';
import type { Role } from '@core/roles-types';
import type { DialogComponentI } from '@core/user-interface-legacy';
import {
  BoxComponent,
  ButtonComponent,
  DialogBodyComponent,
  DialogFooterComponent,
  DialogHeaderComponent,
  DialogRef,
  DividerComponent,
  EmptyStateComponent,
  FormFieldComponent,
  GroupComponent,
  HeadingComponent,
  IconButtonComponent,
  IconComponent,
  InputComponent,
  RtTooltip,
  TabComponent,
  TabsComponent,
} from '@core/user-interface-legacy';
import { includesWithTrim } from '@core/utils-legacy';
import type { Visibility } from '@core/visibility-types';

import { VisibilityAdminItemComponent } from './visibility-admin-item/visibility-admin-item.component';
import type { RoleWithAdmins } from '../../types';

export interface VisibilityDialogData {
  assignedAdmins: number[];
  assignedRoles: number[];
  roleSelectionRequired: boolean;
  candidateId?: number;
  offerId?: number;
  groupUsersByAccess?: boolean | null;
  disabledUserCopy?: string;
}

enum Tab {
  HiringRoles = 'hiring_roles',
  TeamMembers = 'team_members',
}

const maxRenderedItems = 15;

@Component({
  selector: 'rt-visibility-dialog',
  templateUrl: 'visibility-dialog.component.html',
  styleUrls: ['./visibility-dialog.component.less'],
  imports: [
    DialogHeaderComponent,
    DialogBodyComponent,
    BoxComponent,
    FormFieldComponent,
    InputComponent,
    ReactiveFormsModule,
    IconComponent,
    IconButtonComponent,
    TabsComponent,
    TabComponent,
    GroupComponent,
    ButtonComponent,
    DividerComponent,
    InfiniteScrollModule,
    NgTemplateOutlet,
    HeadingComponent,
    VisibilityAdminItemComponent,
    RtTooltip,
    EmptyStateComponent,
    DialogFooterComponent,
    TranslatePipe,
    UpperCasePipe,
  ],
  providers: [SelectAdminsAnalytics],
})
export class VisibilityDialogComponent
  implements OnInit, OnDestroy, DialogComponentI<VisibilityDialogData, Visibility>
{
  private readonly selectAdminsAnalytics = inject(SelectAdminsAnalytics);
  public readonly Tab: typeof Tab = Tab;

  public admins: User[] = [];
  public visibleAdmins: User[] = [];
  public disallowedUsers: User[] = [];
  public visibleDisallowedUsers: User[] = [];
  public allAdmins: User[] = [];
  public currentAdmin: CurrentUser;
  public searchControl = new FormControl<string>('', { nonNullable: true });
  public searchQuery: string = '';
  public assignmentsGroup: UntypedFormGroup = new UntypedFormGroup({});
  public roleAssignmentsGroup: UntypedFormGroup = new UntypedFormGroup({});
  public selectedTab: Tab = Tab.TeamMembers;
  public rolesWithAdmins: RoleWithAdmins[] = [];
  public visibleRolesWithAdmins: RoleWithAdmins[] = [];
  public allRolesWithUsers: RoleWithAdmins[] = [];
  public roleSelectionRequired: boolean = true;
  public candidateId?: number | null;
  public offerId?: number;
  public groupUsersByAccess = false;
  public disabledUserCopy: string;

  private assignedAdmins: number[] = [];
  private assignedRoles: number[] = [];
  private ngOnDestroy$ = new Subject<void>();

  constructor(
    private store: Store,
    private cdRef: ChangeDetectorRef,
    private dialog: DialogRef,
  ) {}

  public ngOnInit(): void {
    this.store
      .select(CurrentUserSelectors.data)
      .pipe(filterBoolean, takeUntil(this.ngOnDestroy$))
      .subscribe(currentAdmin => {
        this.currentAdmin = currentAdmin;
        this.cdRef.detectChanges();
      });

    combineLatest([this.getUsers(), this.getRoles()])
      .pipe(
        distinctUntilChanged(deepEqual),
        filter(([admins, roles]) => !!admins && !!roles),
        takeUntil(this.ngOnDestroy$.asObservable()),
      )
      .subscribe(
        ([admins, roles]: [
          User[] | { usersWithAccess: User[]; usersWithoutAccess: User[] },
          Role[],
        ]) => {
          const groupedAdmins = this.groupedAdminsByRole(
            'usersWithAccess' in admins ? admins.usersWithAccess : admins,
          );

          this.allRolesWithUsers = this.mapAllRolesWithAdmins(roles, groupedAdmins);
          this.allAdmins = this.insertCurrentAdminAtFirstPosition([
            ...('usersWithAccess' in admins ? admins.usersWithAccess : admins),
          ]);
          this.disallowedUsers = 'usersWithoutAccess' in admins ? admins.usersWithoutAccess : [];
          this.allAdmins.forEach(a => this.controlForAdmin(a));
          this.allRolesWithUsers.forEach(a => this.controlForRole(a));
          this.filterRolesAndAdmins(this.searchQuery);
          this.detectChanges();
        },
      );
  }

  public ngOnDestroy(): void {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
  }

  public setDialogData(data: VisibilityDialogData): void {
    this.assignedAdmins = data.assignedAdmins;
    this.assignedRoles = data.assignedRoles;
    this.roleSelectionRequired = !!data.roleSelectionRequired;
    this.candidateId = data.candidateId;
    this.groupUsersByAccess = !!data.groupUsersByAccess;
    this.offerId = data.offerId;
    this.disabledUserCopy =
      data.disabledUserCopy || 'app.candidate.admin_select.no_access.tooltip_share_on_the_go';
  }

  public onScroll(): void {
    this.visibleAdmins = this.admins.slice(0, this.visibleAdmins.length + maxRenderedItems);
    this.visibleRolesWithAdmins = this.rolesWithAdmins.slice(
      0,
      this.visibleRolesWithAdmins.length + maxRenderedItems,
    );
    const filteredDisallowedUsers = this.filterAdminsByQuery(
      this.disallowedUsers,
      this.searchQuery,
    );
    this.visibleDisallowedUsers = filteredDisallowedUsers.slice(
      0,
      filteredDisallowedUsers.length + maxRenderedItems,
    );
    this.detectChanges();
  }

  public onSearchQueryChange(query: string): void {
    this.searchQuery = query;
    this.filterRolesAndAdmins(query);
    this.detectChanges();
  }

  public isSearching(): boolean {
    return !!this.searchControl.value && this.searchControl.value.length > 0;
  }

  public cancelSearch(): void {
    this.searchControl.reset('');
    this.onSearchQueryChange('');
  }

  public trackById<T extends { id: number }>(index: number, { id }: T): number {
    return id;
  }

  public controlForAdmin(admin: User): UntypedFormControl {
    if (!this.assignmentsGroup.controls[admin.id]) {
      const defaultValue = this.assignedAdmins.indexOf(admin.id) < 0 ? false : admin.id;
      const formValue = admin.id === this.currentAdmin.id ? admin.id : defaultValue;
      const control = new UntypedFormControl(formValue);
      if (admin.id === this.currentAdmin.id) {
        control.disable();
      }
      this.assignmentsGroup.addControl(admin.id.toString(), control);
    }
    return <UntypedFormControl>this.assignmentsGroup.controls[admin.id];
  }

  public controlForRole(roleWithAdmins: RoleWithAdmins): UntypedFormControl {
    if (!this.roleAssignmentsGroup.controls[roleWithAdmins.id]) {
      const defaultValue =
        this.assignedRoles.indexOf(roleWithAdmins.id) < 0 ? false : roleWithAdmins.id;
      const formValue = roleWithAdmins.isAdmin ? roleWithAdmins.id : defaultValue;
      const control = new UntypedFormControl(formValue);
      if (this.roleSelectionRequired && roleWithAdmins.isAdmin) {
        control.disable();
      }
      this.roleAssignmentsGroup.addControl(roleWithAdmins.id.toString(), control);
    }
    return <UntypedFormControl>this.roleAssignmentsGroup.controls[roleWithAdmins.id];
  }

  public toggleAssignment(admin: User): void {
    if (admin.id === this.currentAdmin.id) {
      return;
    }
    const control = this.assignmentsGroup.controls[admin.id];
    control.setValue(control.value ? false : admin.id);
    control.markAsDirty();
    this.detectChanges();
  }

  public toggleRoleAssignment(roleWithAdmins: RoleWithAdmins): void {
    if (roleWithAdmins.isAdmin) {
      return;
    }
    const control = this.roleAssignmentsGroup.controls[roleWithAdmins.id];
    control.setValue(control.value ? false : roleWithAdmins.id);
    control.markAsDirty();
    this.detectChanges();
  }

  public selectAll(): void {
    this.selectAdminsAnalytics.selectAll();
    if (this.selectedTab === Tab.TeamMembers) {
      this.assignmentsGroup.setValue(
        this.admins.reduce((object, admin) => ({ ...object, [admin.id]: admin.id }), {}),
      );
    } else {
      this.roleAssignmentsGroup.setValue(
        this.rolesWithAdmins.reduce((object, role) => ({ ...object, [role.id]: role.id }), {}),
      );
    }
    this.detectChanges();
  }

  public selectNone(): void {
    this.selectAdminsAnalytics.selectNone();
    if (this.selectedTab === Tab.TeamMembers) {
      this.assignmentsGroup.reset();

      this.allAdmins.forEach(admin => {
        if (admin.id === this.currentAdmin.id) {
          this.assignmentsGroup.controls[admin.id].setValue(admin.id);
        }
      });
    } else {
      this.roleAssignmentsGroup.reset();

      if (this.roleSelectionRequired) {
        this.allRolesWithUsers.forEach(role => {
          if (role.isAdmin) {
            this.roleAssignmentsGroup.controls[role.id].setValue(role.id);
          }
        });
      }
    }
    this.detectChanges();
  }

  public detectChanges(): void {
    this.cdRef.detectChanges();
  }

  public selectTab(selectedTab: Tab): void {
    this.selectedTab = selectedTab;
    this.detectChanges();
  }

  public onCancelClick(): void {
    this.dialog.close();
  }

  public onSaveClick(): void {
    this.dialog.close(this.collectResults());
  }

  public collectResults() {
    return {
      roleIds: Object.values(this.roleAssignmentsGroup.controls)
        .map(control => control.value)
        .filter(v => v),
      level: 'protected',
      adminIds: Object.values(this.assignmentsGroup.controls)
        .map(control => control.value)
        .filter(v => v),
    };
  }

  private groupedAdminsByRole(admins: User[]): Record<string, User[]> {
    return admins.reduce<Record<string, User[]>>(
      (groups, admin) => ({
        ...groups,
        ...{ [admin.roleId]: [...(groups[admin.roleId] || []), admin] },
      }),
      {},
    );
  }

  private mapAllRolesWithAdmins(
    roles: Role[],
    groupedAdmins: Record<string, User[]>,
  ): RoleWithAdmins[] {
    return roles.map(r => ({
      ...r,
      id: r.id!,
      isAdmin: r.admin,
      admins: groupedAdmins[r.id!] || [],
      count: (groupedAdmins[r.id!] && groupedAdmins[r.id!].length) || 0,
    }));
  }

  private filterRolesByQuery(roles: RoleWithAdmins[], searchQuery: string): RoleWithAdmins[] {
    return roles.filter(r => includesWithTrim(r.name, searchQuery));
  }

  private filterAdminsByQuery(admins: User[], searchQuery: string): User[] {
    return admins.filter(a => includesWithTrim(`${a.firstName} ${a.lastName}` || '', searchQuery));
  }

  private insertCurrentAdminAtFirstPosition(admins: User[]): User[] {
    return [
      ...admins.filter(a => a.id === this.currentAdmin.id),
      ...admins.filter(a => a.id !== this.currentAdmin.id),
    ];
  }

  private filterRolesAndAdmins(query: string): void {
    this.admins = this.filterAdminsByQuery(this.allAdmins, query);
    this.rolesWithAdmins = this.filterRolesByQuery(this.allRolesWithUsers, query);
    this.visibleAdmins = this.admins.slice(0, maxRenderedItems);
    this.visibleRolesWithAdmins = this.rolesWithAdmins.slice(0, maxRenderedItems);
    this.visibleDisallowedUsers = this.filterAdminsByQuery(this.disallowedUsers, query).slice(
      0,
      maxRenderedItems,
    );
  }

  private getUsers(): Observable<User[] | { usersWithAccess: User[]; usersWithoutAccess: User[] }> {
    if (this.isCandidateCtx()) {
      return this.store.select(FullCandidateSelectors.commonUsersGrouped(this.candidateId!));
    }

    if (this.isOfferCtx()) {
      return this.store.select(
        FullCandidateSelectors.offerUsersGrouped({ offerId: this.offerId! }),
      );
    }

    return this.store.select(UserSelectors.entities());
  }

  private getRoles(): Observable<Role[]> {
    const roles$ = this.store.select(RoleSelectors.entities());

    if (this.isCandidateCtx()) {
      return this.store.select(FullCandidateSelectors.byId(this.candidateId!)).pipe(
        filterDefined,
        map(candidate => candidate.isHired),
        switchMap(isHired =>
          isHired
            ? roles$.pipe(
                map(role =>
                  role.filter(role => role.abilities.includes(Ability.ViewHiredCandidates)),
                ),
              )
            : roles$,
        ),
      );
    }

    return roles$;
  }

  private isCandidateCtx(): boolean {
    return this.groupUsersByAccess && !!this.candidateId;
  }

  private isOfferCtx(): boolean {
    return this.groupUsersByAccess && !this.candidateId && !!this.offerId;
  }
}
